import React, { useState, useEffect } from 'react'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  AdminPanelSettings,
  Person,
  Description,
  CheckCircle,
  Group,
  ArrowBack,
  RestartAlt,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import axios from 'axios'

// API setup
const api = axios.create({
  baseURL: (import.meta.env.VITE_BACKEND_URL || '') + '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Simple auth token storage
let authToken = localStorage.getItem('admin-token')

api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`
  }
  return config
})

function App() {
  // Tab state
  const [currentTab, setCurrentTab] = useState(0)
  
  // User management state
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalUsers, setTotalUsers] = useState(0)
  const [isAuthenticated, setIsAuthenticated] = useState(!!authToken)

  // Prompt management state
  const [prompts, setPrompts] = useState([])
  const [promptsLoading, setPromptsLoading] = useState(false)
  const [promptDialogOpen, setPromptDialogOpen] = useState(false)
  const [editPrompt, setEditPrompt] = useState(null)
  const [promptPage, setPromptPage] = useState(0)
  const [promptRowsPerPage, setPromptRowsPerPage] = useState(10)
  const [totalPrompts, setTotalPrompts] = useState(0)

  // Persona management state
  const [personas, setPersonas] = useState([])
  const [personasLoading, setPersonasLoading] = useState(false)
  const [personaDialogOpen, setPersonaDialogOpen] = useState(false)
  const [editPersona, setEditPersona] = useState(null)
  const [selectedPersona, setSelectedPersona] = useState(null) // detail view
  // Persona-bound prompts state
  const [personaPrompts, setPersonaPrompts] = useState([])
  const [personaPromptsLoading, setPersonaPromptsLoading] = useState(false)
  const [personaPromptDialogOpen, setPersonaPromptDialogOpen] = useState(false)
  const [editPersonaPrompt, setEditPersonaPrompt] = useState(null)
  const [personaPromptPage, setPersonaPromptPage] = useState(0)
  const [personaPromptRowsPerPage, setPersonaPromptRowsPerPage] = useState(10)
  const [totalPersonaPrompts, setTotalPersonaPrompts] = useState(0)

  // Separate form instances for users, prompts, personas, and persona prompts
  const userForm = useForm()
  const promptForm = useForm()
  const personaForm = useForm()
  const personaPromptForm = useForm()
  
  // Destructure for backward compatibility in some places
  const { register: registerUser, handleSubmit: handleSubmitUser, reset: resetUser, unregister: unregisterUser, formState: { errors: userErrors } } = userForm
  const { register: registerPrompt, handleSubmit: handleSubmitPrompt, reset: resetPrompt, control: controlPrompt, formState: { errors: promptErrors } } = promptForm
  const { register: registerPersona, handleSubmit: handleSubmitPersona, reset: resetPersona, control: controlPersona, formState: { errors: personaErrors } } = personaForm
  const { register: registerPersonaPrompt, handleSubmit: handleSubmitPersonaPrompt, reset: resetPersonaPrompt, control: controlPersonaPrompt, formState: { errors: personaPromptErrors } } = personaPromptForm

  // Login for admin
  const [loginData, setLoginData] = useState({ email: '', password: '' })

  const handleLogin = async (e) => {
    e.preventDefault()
    try {
      const response = await api.post('/auth/login', loginData)
      if (response.data.data.user.isAdmin) {
        authToken = response.data.data.token
        localStorage.setItem('admin-token', authToken)
        setIsAuthenticated(true)
        toast.success('Admin login successful')
        loadUsers()
      } else {
        toast.error('Admin access required')
      }
    } catch (error) {
      toast.error('Login failed')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('admin-token')
    authToken = null
    setIsAuthenticated(false)
    setUsers([])
  }

  const loadUsers = async () => {
    if (!isAuthenticated) return
    
    setLoading(true)
    try {
      const response = await api.get('/users', {
        params: { page: page + 1, limit: rowsPerPage }
      })
      
      setUsers(response.data.data.users)
      setTotalUsers(response.data.data.pagination.totalUsers)
    } catch (error) {
      console.error('Error loading users:', error)
      if (error.response?.status === 401) {
        handleLogout()
      } else {
        toast.error('Failed to load users')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      loadUsers()
    }
  }, [page, rowsPerPage, isAuthenticated])

  const handleCreateUser = async (data) => {
    try {
      console.log('Creating user, Data:', data)
      const response = await api.post('/users', data)
      console.log('Create user response:', response.data)
      toast.success('User created successfully')
      setDialogOpen(false)
      setEditUser(null)
      loadUsers()
    } catch (error) {
      console.error('Create user error:', error)
      toast.error(error.response?.data?.error || 'Failed to create user')
    }
  }

  const handleUpdateUser = async (data) => {
    try {
      console.log('Updating user:', editUser.id, 'Data:', data)
      // Remove password if empty (keep existing password)
      const updateData = { ...data }
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password
      }
      const response = await api.put(`/users/${editUser.id}`, updateData)
      console.log('Update user response:', response.data)
      toast.success('User updated successfully')
      setDialogOpen(false)
      setEditUser(null)
      loadUsers()
    } catch (error) {
      console.error('Update user error:', error)
      toast.error(error.response?.data?.error || 'Failed to update user')
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return
    
    try {
      await api.delete(`/users/${userId}`)
      toast.success('User deleted successfully')
      loadUsers()
    } catch (error) {
      toast.error('Failed to delete user')
    }
  }

  // Reset user state
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  const [resetTarget, setResetTarget] = useState(null)

  const handleResetUser = async () => {
    if (!resetTarget) return
    try {
      await api.post(`/users/${resetTarget.id}/reset`)
      toast.success(`User ${resetTarget.email} reset successfully — all data wiped`)
      setResetConfirmOpen(false)
      setResetTarget(null)
      loadUsers()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset user')
    }
  }

  const openCreateDialog = () => {
    console.log('Opening create user dialog')
    setEditUser(null)
    setDialogOpen(true)
  }

  const openEditDialog = (user) => {
    console.log('Opening edit user dialog for:', user)
    setEditUser(user)
    setDialogOpen(true)
  }

  // Reset form when user dialog opens/closes or edit mode changes
  useEffect(() => {
    if (dialogOpen) {
      console.log('User dialog opened. Edit mode:', !!editUser, 'User ID:', editUser?.id)
      if (editUser) {
        // Editing existing user
        resetUser({
          firstName: editUser.first_name,
          lastName: editUser.last_name,
          email: editUser.email,
          password: '', // Allow password updates
          isAdmin: editUser.is_admin,
        })
      } else {
        // Creating new user - clear all fields
        resetUser({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          isAdmin: false,
        })
      }
    }
  }, [dialogOpen, editUser, resetUser])

  // Prompt Management Functions
  const loadPrompts = async () => {
    if (!isAuthenticated) return
    
    setPromptsLoading(true)
    try {
      const response = await api.get('/prompts', {
        params: {
          page: promptPage + 1,
          limit: promptRowsPerPage,
          sortBy: 'created_at',
          sortOrder: 'DESC',
          personaId: 'null'
        }
      })
      
      setPrompts(response.data.data.prompts)
      setTotalPrompts(response.data.data.total)
    } catch (error) {
      console.error('Error loading prompts:', error)
      if (error.response?.status === 401) {
        handleLogout()
      } else {
        toast.error('Failed to load prompts')
      }
    } finally {
      setPromptsLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated && currentTab === 1) {
      loadPrompts()
    }
  }, [promptPage, promptRowsPerPage, isAuthenticated, currentTab])

  const handleCreatePrompt = async (data) => {
    try {
      console.log('Creating prompt, Data:', data)
      const response = await api.post('/prompts', {
        name: data.name,
        content: data.content,
        description: data.description,
        comments: data.comments,
        isActive: data.isActive || false
      })
      console.log('Create response:', response.data)
      toast.success('Prompt created successfully')
      setPromptDialogOpen(false)
      setEditPrompt(null)
      loadPrompts()
    } catch (error) {
      console.error('Create error:', error)
      toast.error(error.response?.data?.error || 'Failed to create prompt')
    }
  }

  const handleUpdatePrompt = async (data) => {
    try {
      console.log('Updating prompt:', editPrompt.id, 'Data:', data)
      const response = await api.put(`/prompts/${editPrompt.id}`, {
        name: data.name,
        content: data.content,
        description: data.description,
        comments: data.comments,
        isActive: data.isActive
      })
      console.log('Update response:', response.data)
      toast.success('Prompt updated successfully')
      setPromptDialogOpen(false)
      setEditPrompt(null)
      loadPrompts()
    } catch (error) {
      console.error('Update error:', error)
      toast.error(error.response?.data?.error || 'Failed to update prompt')
    }
  }

  const handleActivatePrompt = async (promptId) => {
    try {
      await api.post(`/prompts/${promptId}/activate`)
      toast.success('Prompt activated successfully')
      loadPrompts()
    } catch (error) {
      toast.error('Failed to activate prompt')
    }
  }

  const handleDeletePrompt = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return
    
    try {
      await api.delete(`/prompts/${promptId}`)
      toast.success('Prompt deleted successfully')
      loadPrompts()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete prompt')
    }
  }

  const openCreatePromptDialog = () => {
    setEditPrompt(null)
    setPromptDialogOpen(true)
  }

  const openEditPromptDialog = (prompt) => {
    console.log('Opening edit dialog for prompt:', prompt)
    setEditPrompt(prompt)
    setPromptDialogOpen(true)
  }

  // Reset form when prompt dialog opens/closes or edit mode changes
  useEffect(() => {
    if (promptDialogOpen) {
      console.log('Prompt dialog opened. Edit mode:', !!editPrompt, 'Prompt ID:', editPrompt?.id)
      if (editPrompt) {
        // Editing existing prompt
        resetPrompt({
          name: editPrompt.name,
          content: editPrompt.content,
          description: editPrompt.description,
          comments: editPrompt.comments,
          isActive: editPrompt.is_active
        })
      } else {
        // Creating new prompt - clear all fields
        resetPrompt({
          name: '',
          content: '',
          description: '',
          comments: '',
          isActive: false,
        })
      }
    }
  }, [promptDialogOpen, editPrompt, resetPrompt])

  // Persona Management Functions
  const loadPersonas = async () => {
    if (!isAuthenticated) return
    setPersonasLoading(true)
    try {
      const response = await api.get('/admin/personas')
      setPersonas(response.data.data.personas)
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout()
      } else {
        toast.error('Failed to load personas')
      }
    } finally {
      setPersonasLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated && currentTab === 2) {
      loadPersonas()
    }
  }, [isAuthenticated, currentTab])

  const handleCreatePersona = async (data) => {
    try {
      await api.post('/admin/personas', {
        name: data.name,
        bio: data.bio,
        pronouns: data.pronouns,
        tone: data.tone,
        sortOrder: parseInt(data.sortOrder) || 0,
        isActive: data.isActive ?? true,
        greetingHeader: data.greetingHeader || null,
        greetingBody: data.greetingBody || null,
        emoji: data.emoji || null
      })
      toast.success('Persona created successfully')
      setPersonaDialogOpen(false)
      setEditPersona(null)
      loadPersonas()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create persona')
    }
  }

  const handleUpdatePersona = async (data) => {
    try {
      const response = await api.put(`/admin/personas/${editPersona.id}`, {
        name: data.name,
        bio: data.bio,
        pronouns: data.pronouns,
        tone: data.tone,
        sortOrder: parseInt(data.sortOrder) || 0,
        isActive: data.isActive,
        greetingHeader: data.greetingHeader || null,
        greetingBody: data.greetingBody || null,
        emoji: data.emoji || null
      })
      toast.success('Persona updated successfully')
      setPersonaDialogOpen(false)
      setEditPersona(null)
      // Update selectedPersona if we're editing the currently viewed one
      if (selectedPersona?.id === editPersona.id) {
        setSelectedPersona(response.data.data.persona)
      }
      loadPersonas()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update persona')
    }
  }

  const handleDeletePersona = async (personaId) => {
    if (!window.confirm('Are you sure? This will also delete all persona-bound prompts.')) return
    try {
      await api.delete(`/admin/personas/${personaId}`)
      toast.success('Persona deleted successfully')
      if (selectedPersona?.id === personaId) {
        setSelectedPersona(null)
      }
      loadPersonas()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete persona')
    }
  }

  const openCreatePersonaDialog = () => {
    setEditPersona(null)
    setPersonaDialogOpen(true)
  }

  const openEditPersonaDialog = (persona) => {
    setEditPersona(persona)
    setPersonaDialogOpen(true)
  }

  useEffect(() => {
    if (personaDialogOpen) {
      if (editPersona) {
        resetPersona({
          name: editPersona.name,
          bio: editPersona.bio || '',
          pronouns: editPersona.pronouns || '',
          tone: editPersona.tone || '',
          sortOrder: editPersona.sort_order || 0,
          isActive: editPersona.is_active,
          greetingHeader: editPersona.greeting_header || '',
          greetingBody: editPersona.greeting_body || '',
          emoji: editPersona.emoji || ''
        })
      } else {
        resetPersona({
          name: '',
          bio: '',
          pronouns: '',
          tone: '',
          sortOrder: 0,
          isActive: true,
          greetingHeader: '',
          greetingBody: '',
          emoji: ''
        })
      }
    }
  }, [personaDialogOpen, editPersona, resetPersona])

  // Persona-bound Prompt Functions
  const loadPersonaPrompts = async (personaId) => {
    if (!isAuthenticated || !personaId) return
    setPersonaPromptsLoading(true)
    try {
      const response = await api.get('/prompts', {
        params: {
          page: personaPromptPage + 1,
          limit: personaPromptRowsPerPage,
          sortBy: 'created_at',
          sortOrder: 'DESC',
          personaId
        }
      })
      setPersonaPrompts(response.data.data.prompts)
      setTotalPersonaPrompts(response.data.data.total)
    } catch (error) {
      toast.error('Failed to load persona prompts')
    } finally {
      setPersonaPromptsLoading(false)
    }
  }

  useEffect(() => {
    if (selectedPersona) {
      loadPersonaPrompts(selectedPersona.id)
    }
  }, [selectedPersona, personaPromptPage, personaPromptRowsPerPage])

  const handleCreatePersonaPrompt = async (data) => {
    try {
      await api.post('/prompts', {
        name: data.name,
        content: data.content,
        description: data.description,
        comments: data.comments,
        isActive: data.isActive || false,
        personaId: selectedPersona.id
      })
      toast.success('Prompt created successfully')
      setPersonaPromptDialogOpen(false)
      setEditPersonaPrompt(null)
      loadPersonaPrompts(selectedPersona.id)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create prompt')
    }
  }

  const handleUpdatePersonaPrompt = async (data) => {
    try {
      await api.put(`/prompts/${editPersonaPrompt.id}`, {
        name: data.name,
        content: data.content,
        description: data.description,
        comments: data.comments,
        isActive: data.isActive
      })
      toast.success('Prompt updated successfully')
      setPersonaPromptDialogOpen(false)
      setEditPersonaPrompt(null)
      loadPersonaPrompts(selectedPersona.id)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update prompt')
    }
  }

  const handleActivatePersonaPrompt = async (promptId) => {
    try {
      await api.post(`/prompts/${promptId}/activate`)
      toast.success('Prompt activated successfully')
      loadPersonaPrompts(selectedPersona.id)
    } catch (error) {
      toast.error('Failed to activate prompt')
    }
  }

  const handleDeletePersonaPrompt = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return
    try {
      await api.delete(`/prompts/${promptId}`)
      toast.success('Prompt deleted successfully')
      loadPersonaPrompts(selectedPersona.id)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete prompt')
    }
  }

  useEffect(() => {
    if (personaPromptDialogOpen) {
      if (editPersonaPrompt) {
        resetPersonaPrompt({
          name: editPersonaPrompt.name,
          content: editPersonaPrompt.content,
          description: editPersonaPrompt.description,
          comments: editPersonaPrompt.comments,
          isActive: editPersonaPrompt.is_active
        })
      } else {
        resetPersonaPrompt({
          name: '',
          content: '',
          description: '',
          comments: '',
          isActive: false
        })
      }
    }
  }, [personaPromptDialogOpen, editPersonaPrompt, resetPersonaPrompt])

  if (!isAuthenticated) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography variant="h4" align="center" gutterBottom>
              Coach AI Admin
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Admin access required
            </Typography>
            
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                margin="normal"
                required
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Sign In as Admin
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static">
        <Toolbar>
          <AdminPanelSettings sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Coach AI Admin Dashboard
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label="Users" icon={<Person />} iconPosition="start" />
            <Tab label="System Prompts" icon={<Description />} iconPosition="start" />
            <Tab label="Personas" icon={<Group />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* User Management Tab */}
        {currentTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">User Management</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openCreateDialog}
              >
                Add User
              </Button>
            </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_admin ? 'Admin' : 'User'}
                            color={user.is_admin ? 'error' : 'default'}
                            size="small"
                            icon={user.is_admin ? <AdminPanelSettings /> : <Person />}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(user)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          {!user.is_admin && (
                            <IconButton
                              size="small"
                              onClick={() => { setResetTarget(user); setResetConfirmOpen(true) }}
                              color="warning"
                              title="Reset user (wipe all data)"
                            >
                              <RestartAlt />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user.id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalUsers}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
          </Paper>
        )}

        {/* Prompts Management Tab */}
        {currentTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5">System Prompts</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openCreatePromptDialog}
              >
                Add Prompt
              </Button>
            </Box>

            {promptsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Version</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {prompts.map((prompt) => (
                        <TableRow key={prompt.id} sx={{ backgroundColor: prompt.is_active ? '#f0f8ff' : 'inherit' }}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {prompt.is_active && <CheckCircle sx={{ color: 'green', mr: 1, fontSize: 18 }} />}
                              <Typography variant="body2" fontWeight={prompt.is_active ? 'bold' : 'normal'}>
                                {prompt.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                              {prompt.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={`v${prompt.version}`} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={prompt.is_active ? 'Active' : 'Inactive'}
                              color={prompt.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {prompt.creator_first_name ? `${prompt.creator_first_name} ${prompt.creator_last_name}` : '-'}
                          </TableCell>
                          <TableCell>
                            {new Date(prompt.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            {!prompt.is_active && (
                              <IconButton
                                size="small"
                                onClick={() => handleActivatePrompt(prompt.id)}
                                color="success"
                                title="Set as Active"
                              >
                                <CheckCircle />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => openEditPromptDialog(prompt)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePrompt(prompt.id)}
                              color="error"
                              disabled={prompt.is_active}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalPrompts}
                  page={promptPage}
                  onPageChange={(_, newPage) => setPromptPage(newPage)}
                  rowsPerPage={promptRowsPerPage}
                  onRowsPerPageChange={(e) => setPromptRowsPerPage(parseInt(e.target.value, 10))}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </>
            )}
          </Paper>
        )}

        {/* Personas Tab */}
        {currentTab === 2 && (
          <Paper sx={{ p: 3 }}>
            {selectedPersona ? (
              // Persona Detail View — shows persona-bound prompts
              <>
                <Box display="flex" alignItems="center" mb={3}>
                  <IconButton onClick={() => { setSelectedPersona(null); setPersonaPromptPage(0) }} sx={{ mr: 1 }}>
                    <ArrowBack />
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5">
                      {selectedPersona.name}
                      <Chip
                        label={selectedPersona.is_active ? 'Active' : 'Inactive'}
                        color={selectedPersona.is_active ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPersona.pronouns} · {selectedPersona.tone}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => { setEditPersonaPrompt(null); setPersonaPromptDialogOpen(true) }}
                  >
                    Add Prompt
                  </Button>
                </Box>

                {personaPromptsLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Version</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created By</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {personaPrompts.map((prompt) => (
                            <TableRow key={prompt.id} sx={{ backgroundColor: prompt.is_active ? '#f0f8ff' : 'inherit' }}>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  {prompt.is_active && <CheckCircle sx={{ color: 'green', mr: 1, fontSize: 18 }} />}
                                  <Typography variant="body2" fontWeight={prompt.is_active ? 'bold' : 'normal'}>
                                    {prompt.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                  {prompt.description || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={`v${prompt.version}`} size="small" />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={prompt.is_active ? 'Active' : 'Inactive'}
                                  color={prompt.is_active ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {prompt.creator_first_name ? `${prompt.creator_first_name} ${prompt.creator_last_name}` : '-'}
                              </TableCell>
                              <TableCell>
                                {new Date(prompt.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="right">
                                {!prompt.is_active && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleActivatePersonaPrompt(prompt.id)}
                                    color="success"
                                    title="Set as Active"
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => { setEditPersonaPrompt(prompt); setPersonaPromptDialogOpen(true) }}
                                  color="primary"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeletePersonaPrompt(prompt.id)}
                                  color="error"
                                  disabled={prompt.is_active}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      component="div"
                      count={totalPersonaPrompts}
                      page={personaPromptPage}
                      onPageChange={(_, newPage) => setPersonaPromptPage(newPage)}
                      rowsPerPage={personaPromptRowsPerPage}
                      onRowsPerPageChange={(e) => setPersonaPromptRowsPerPage(parseInt(e.target.value, 10))}
                      rowsPerPageOptions={[5, 10, 25]}
                    />
                  </>
                )}
              </>
            ) : (
              // Personas List View
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h5">Personas</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openCreatePersonaDialog}
                  >
                    Add Persona
                  </Button>
                </Box>

                {personasLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Pronouns</TableCell>
                          <TableCell>Tone</TableCell>
                          <TableCell>Greeting</TableCell>
                          <TableCell>Sort Order</TableCell>
                          <TableCell>Active</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {personas.map((persona) => (
                          <TableRow key={persona.id}>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' }, display: 'flex', alignItems: 'center', gap: '6px' }}
                                onClick={() => setSelectedPersona(persona)}
                              >
                                {persona.emoji && <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{persona.emoji}</span>}
                                {persona.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{persona.pronouns || '-'}</TableCell>
                            <TableCell>{persona.tone || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>
                              {persona.greeting_header ? (
                                <Typography variant="body2" noWrap title={`${persona.greeting_header} — ${persona.greeting_body || ''}`}>
                                  {persona.greeting_header}
                                </Typography>
                              ) : '-'}
                            </TableCell>
                            <TableCell>{persona.sort_order}</TableCell>
                            <TableCell>
                              <Chip
                                label={persona.is_active ? 'Active' : 'Inactive'}
                                color={persona.is_active ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => openEditPersonaDialog(persona)}
                                color="primary"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeletePersona(persona.id)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Paper>
        )}
      </Container>

      {/* User Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          setDialogOpen(false)
          setEditUser(null)
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmitUser(editUser ? handleUpdateUser : handleCreateUser)} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="First Name"
              margin="normal"
              {...registerUser('firstName', { required: 'First name is required' })}
              error={!!userErrors.firstName}
              helperText={userErrors.firstName?.message}
            />
            <TextField
              fullWidth
              label="Last Name"
              margin="normal"
              {...registerUser('lastName', { required: 'Last name is required' })}
              error={!!userErrors.lastName}
              helperText={userErrors.lastName?.message}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              {...registerUser('email', { 
                required: 'Email is required',
                pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' }
              })}
              error={!!userErrors.email}
              helperText={userErrors.email?.message}
            />
            <TextField
              fullWidth
              label={editUser ? 'Password (leave blank to keep current)' : 'Password'}
              type="password"
              margin="normal"
              {...registerUser('password', { 
                required: editUser ? false : 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' }
              })}
              error={!!userErrors.password}
              helperText={userErrors.password?.message}
            />
            <Box sx={{ mt: 2 }}>
              <label>
                <input
                  type="checkbox"
                  {...registerUser('isAdmin')}
                  style={{ marginRight: 8 }}
                />
                Admin User
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false)
            setEditUser(null)
          }}>Cancel</Button>
          <Button
            onClick={async (e) => {
              console.log('User button clicked, editUser:', editUser?.id)
              console.log('Form errors:', userErrors)
              e.preventDefault()
              const handler = editUser ? handleUpdateUser : handleCreateUser
              console.log('Calling handleSubmit with handler:', handler.name)
              try {
                await handleSubmitUser(
                  (data) => {
                    console.log('User form validation passed! Data:', data)
                    return handler(data)
                  },
                  (errors) => {
                    console.error('User form validation failed:', errors)
                  }
                )(e)
              } catch (err) {
                console.error('User handleSubmit error:', err)
              }
            }}
            variant="contained"
          >
            {editUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt Dialog */}
      <Dialog 
        open={promptDialogOpen} 
        onClose={() => {
          setPromptDialogOpen(false)
          setEditPrompt(null)
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editPrompt ? `Edit System Prompt${editPrompt?.version ? ` · v${editPrompt.version}` : ''}` : 'Create New System Prompt'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Prompt Name"
              margin="normal"
              {...registerPrompt('name', { required: 'Name is required' })}
              error={!!promptErrors.name}
              helperText={promptErrors.name?.message}
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              multiline
              rows={2}
              {...registerPrompt('description')}
              error={!!promptErrors.description}
              helperText={promptErrors.description?.message}
            />
            <TextField
              fullWidth
              label="Prompt Content"
              margin="normal"
              multiline
              rows={12}
              {...registerPrompt('content', { required: 'Content is required' })}
              error={!!promptErrors.content}
              helperText={promptErrors.content?.message || 'The system prompt that will be sent to the AI'}
            />
            <TextField
              fullWidth
              label="Comments / Notes"
              margin="normal"
              multiline
              rows={3}
              {...registerPrompt('comments')}
              error={!!promptErrors.comments}
              helperText="Internal notes about this prompt version"
            />
            <Box sx={{ mt: 2 }}>
              <Controller
                name="isActive"
                control={controlPrompt}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Set as Active Prompt"
                  />
                )}
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                Making this active will deactivate all other prompts
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => {
              setPromptDialogOpen(false)
              setEditPrompt(null)
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmitPrompt(editPrompt ? handleUpdatePrompt : handleCreatePrompt)}
            variant="contained"
          >
            {editPrompt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Persona Dialog */}
      <Dialog
        open={personaDialogOpen}
        onClose={() => { setPersonaDialogOpen(false); setEditPersona(null) }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editPersona ? 'Edit Persona' : 'Create New Persona'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              margin="normal"
              {...registerPersona('name', { required: 'Name is required' })}
              error={!!personaErrors.name}
              helperText={personaErrors.name?.message}
            />
            <TextField
              fullWidth
              label="Bio"
              margin="normal"
              multiline
              rows={3}
              {...registerPersona('bio')}
            />
            <TextField
              fullWidth
              label="Pronouns"
              margin="normal"
              {...registerPersona('pronouns')}
              helperText="e.g. he/him, she/her, they/them"
            />
            <TextField
              fullWidth
              label="Tone"
              margin="normal"
              {...registerPersona('tone')}
              helperText="e.g. warm, direct, playful"
            />
            <TextField
              fullWidth
              label="Sort Order"
              margin="normal"
              type="number"
              {...registerPersona('sortOrder')}
            />
            <TextField
              fullWidth
              label="Emoji Avatar"
              margin="normal"
              {...registerPersona('emoji')}
              helperText="Single emoji shown as avatar stub, e.g. 🧔"
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              fullWidth
              label="Greeting Header"
              margin="normal"
              {...registerPersona('greetingHeader')}
              helperText="Big text shown on empty dialogue, e.g. 'What is on your mind today?'"
            />
            <TextField
              fullWidth
              label="Greeting Body"
              margin="normal"
              multiline
              rows={2}
              {...registerPersona('greetingBody')}
              helperText="Smaller text below the header"
            />
            <Box sx={{ mt: 2 }}>
              <Controller
                name="isActive"
                control={controlPersona}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Active"
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPersonaDialogOpen(false); setEditPersona(null) }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPersona(editPersona ? handleUpdatePersona : handleCreatePersona)}
            variant="contained"
          >
            {editPersona ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset User Confirmation Dialog */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => { setResetConfirmOpen(false); setResetTarget(null) }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main' }}>⚠️ Reset User</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to reset <strong>{resetTarget?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This will permanently delete all of this user&apos;s data:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li><Typography variant="body2">All chat sessions</Typography></li>
            <li><Typography variant="body2">All messages and conversation history</Typography></li>
            <li><Typography variant="body2">All ratings and feedback</Typography></li>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            The account will be recreated with the same email and password. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetConfirmOpen(false); setResetTarget(null) }}>
            Cancel
          </Button>
          <Button
            onClick={handleResetUser}
            variant="contained"
            color="warning"
          >
            Reset User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Persona-bound Prompt Dialog */}
      <Dialog
        open={personaPromptDialogOpen}
        onClose={() => { setPersonaPromptDialogOpen(false); setEditPersonaPrompt(null) }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editPersonaPrompt
            ? `Edit Prompt${editPersonaPrompt?.version ? ` · v${editPersonaPrompt.version}` : ''}`
            : `New Prompt for ${selectedPersona?.name || 'Persona'}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Prompt Name"
              margin="normal"
              {...registerPersonaPrompt('name', { required: 'Name is required' })}
              error={!!personaPromptErrors.name}
              helperText={personaPromptErrors.name?.message}
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              multiline
              rows={2}
              {...registerPersonaPrompt('description')}
            />
            <TextField
              fullWidth
              label="Prompt Content"
              margin="normal"
              multiline
              rows={12}
              {...registerPersonaPrompt('content', { required: 'Content is required' })}
              error={!!personaPromptErrors.content}
              helperText={personaPromptErrors.content?.message || 'The system prompt for this persona'}
            />
            <TextField
              fullWidth
              label="Comments / Notes"
              margin="normal"
              multiline
              rows={3}
              {...registerPersonaPrompt('comments')}
              helperText="Internal notes about this prompt version"
            />
            <Box sx={{ mt: 2 }}>
              <Controller
                name="isActive"
                control={controlPersonaPrompt}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Set as Active Prompt"
                  />
                )}
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                Making this active will deactivate other prompts for this persona
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPersonaPromptDialogOpen(false); setEditPersonaPrompt(null) }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPersonaPrompt(editPersonaPrompt ? handleUpdatePersonaPrompt : handleCreatePersonaPrompt)}
            variant="contained"
          >
            {editPersonaPrompt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default App 